










































































































import { Component, Inject, Mixins } from "vue-property-decorator";

// Components
import { UpmDac } from "@upmind-widgets/dac";

// Mixins
import Context from "@/mixins/context";

// Enums/Data
import { ShopItemsLayout } from "@/data/shop/enums";
import { QUERY_PARAMS } from "@/data/constants";
import { getCategoryRoute } from "@/data/shop/utils";

// Types
import {
  IStoreProviderData,
  IStoreProviderMethods
} from "@/models/providers/basket/store";
import {
  IBasketProviderData,
  IBasketProviderMethods
} from "@/models/providers/baskets";
import {
  IProvisionProviderMethods,
  IProvisionProviderData
} from "@/models/providers/provisioning";

@Component({
  components: {
    UpmDac,
    "shop-products-list": () =>
      import("@/components/app/global/baskets/basket/shop/shopProductsList.vue")
  }
})
export default class ProductListWrapper extends Mixins(Context) {
  // Inject

  @Inject({ from: "storeProviderData" })
  public readonly $storeProviderData!: () => IStoreProviderData;

  @Inject({ from: "storeProviderMethods" })
  public readonly $storeMethods!: IStoreProviderMethods;

  @Inject({ from: "basketProviderData" })
  public readonly $basketProviderData!: () => IBasketProviderData;

  @Inject({ from: "basketProviderMethods" })
  public readonly $basketMethods!: IBasketProviderMethods;

  @Inject({ from: "provisionProviderMethods" })
  public readonly $provisionProviderMethods!: IProvisionProviderMethods;

  @Inject({ from: "provisionProviderData" })
  public readonly $provisionProviderData!: () => IProvisionProviderData;

  // Computed

  public get $storeData() {
    return this.$storeProviderData();
  }

  public get basketData() {
    return this.$basketProviderData();
  }

  public get provisionData() {
    return this.$provisionProviderData();
  }

  public get selectedCategory() {
    return this.$_.get(this.$storeData, "selectedCategory", null);
  }

  public get items() {
    if (!this.$_.get(this.selectedCategory, "products_count", 0)) {
      return this.$_.map(
        this.$storeData.selectedCategory.subcategories,
        subcategory => {
          return {
            ...subcategory,
            route: getCategoryRoute(this, {
              [QUERY_PARAMS.CATEGORY_ID]: `${subcategory.id}`
            })
          };
        }
      );
    }

    return this.$storeData.products;
  }

  public get productsImageFilter() {
    return this.$_.some(this.$storeData.products, product => {
      return this.$_.get(product, "image");
    });
  }

  public get showDescription() {
    const { searchValue, products } = this.$storeData;
    if (!!searchValue || !products.length) return false;
    return this.categoryDescription;
  }

  public get showNoResults() {
    const { productsAreFiltering, searchValue, products } = this.$storeData;
    return !productsAreFiltering && !!searchValue && !products.length;
  }

  public get showNoSellableProducts() {
    if (this.$storeData.hasDacWidget) return false;
    if (this.$storeData.productsAreFiltering) return false;
    if (this.$storeData.searchValue) return false;
    return (
      !this.$storeData.products.length &&
      !this.$_.get(this.selectedCategory, "subcategories", []).length
    );
  }

  public get itemsLayout() {
    return this.$storeData.products.length < 3
      ? ShopItemsLayout.HORIZONTAL
      : ShopItemsLayout.VERTICAL;
  }

  public get categoryName() {
    return (
      this.$_.get(this.$storeData, "selectedCategory.name_translated") ||
      this.$_.get(this.$storeData, "selectedCategory.name")
    );
  }

  public get categoryDescription() {
    return (
      this.$_.get(
        this.$storeData,
        "selectedCategory.short_description_translated"
      ) ||
      this.$_.get(this.$storeData, "selectedCategory.short_description") ||
      this.$_.get(this.$storeData, "selectedCategory.description_translated") ||
      this.$_.get(this.$storeData, "selectedCategory.description")
    );
  }

  public get authToken() {
    return this.$_.get(
      this.$store,
      `state.auth.${this.context}.token.access_token`,
      ""
    );
  }

  // Methods

  public reloadData() {
    this.$storeData.productsAreReloading = true;
    this.$storeMethods.loadCategoryProducts(
      this.$storeData.pathCategoryId,
      null
    );
  }

  public async addToBasket({ _product, sld = "" }) {
    if (!this.selectedCategory?.products_count) return;
    const isConfigurable = this.$basketMethods.isConfigurable(_product);
    const isProvisionable = await this.$provisionProviderMethods.isProvisionable(
      _product.id,
      this.basketData.clientId
    );
    // If configurable or provisionable
    if (isConfigurable || isProvisionable) {
      // Route to product configuration
      return this.$storeMethods.configureProduct(
        this.$_.merge(
          {
            [QUERY_PARAMS.PRODUCT_ID]: _product.id
          },
          sld ? { [`${QUERY_PARAMS.PRODUCT_FIELDS}[sld]`]: sld } : {},
          _product.sub_product_id
            ? { [QUERY_PARAMS.SUBPRODUCT_IDS]: _product.sub_product_id }
            : {}
        )
      );
    }

    // Quick add to basket
    await this.$basketMethods.quickAddToBasket(_product);

    this.$basketMethods.showCrossSellsModal(
      _product.id,
      this.$provisionProviderMethods.isProvisionable
    );
  }

  public searchByNameDebounce = this.$_.debounce(this.searchByName, 500);

  public searchByName(value) {
    this.$storeMethods.searchByName(value);
  }
}
